/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Toast — Premium Notification Toast
 * ============================================================================
 *
 * Built on @radix-ui/react-toast. Features: icon background circles,
 * gradient progress bar, brand accent border, spring entrance animation,
 * round close button, prefers-reduced-motion support.
 *
 * ============================================================================
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { NotificationType } from '@/stores/notificationStore';

interface ToastProps {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
}

const getIcon = (type: NotificationType) => {
  const iconProps = { size: 18, strokeWidth: 2.5 };
  switch (type) {
    case 'success': return <CheckCircle {...iconProps} />;
    case 'error':   return <AlertCircle {...iconProps} />;
    case 'warning': return <AlertTriangle {...iconProps} />;
    case 'info':    return <Info {...iconProps} />;
  }
};

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration,
  onClose,
}) => {
  const [progress, setProgress] = useState(100);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const msgRef = useRef<HTMLDivElement>(null);

  const handleDoubleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text
      if (msgRef.current) {
        const range = document.createRange();
        range.selectNodeContents(msgRef.current);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    }
  }, [message]);

  useEffect(() => {
    if (!duration) return;
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, [duration]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setTimeout(() => onClose(id), 100);
  };

  return (
    <>
      <style>{toastStyles}</style>
      <ToastPrimitive.Root
        className={`tx-toast tx-${type}`}
        open={open}
        onOpenChange={handleOpenChange}
        duration={duration || Infinity}
      >
        {/* Icon with tinted background */}
        <div className={`tx-icon-box tx-icon-${type}`}>
          {getIcon(type)}
        </div>

        {/* Content — double-click to copy */}
        <div className={`tx-content ${copied ? 'tx-copied-flash' : ''}`} onDoubleClick={handleDoubleClick}>
          <ToastPrimitive.Title className="tx-title">{title}</ToastPrimitive.Title>
          <ToastPrimitive.Description className="tx-message" ref={msgRef}>{message}</ToastPrimitive.Description>
          {copied && <span className="tx-copied-badge">Copied!</span>}
        </div>

        {/* Close */}
        <ToastPrimitive.Close className="tx-close" aria-label="Close">
          <X size={14} />
        </ToastPrimitive.Close>

        {/* Progress */}
        {duration && (
          <div className="tx-progress">
            <div className={`tx-progress-bar tx-bar-${type}`} style={{ width: `${progress}%` }} />
          </div>
        )}
      </ToastPrimitive.Root>
    </>
  );
};

const toastStyles = `
  /* ── Toast root ── */
  .tx-toast {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    padding-right: 40px;
    background: var(--color-bg-secondary);
    border-radius: 3px;
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    box-shadow:
      0 8px 30px rgba(0,0,0,0.2),
      0 2px 8px rgba(0,0,0,0.1);
    overflow: hidden;
    min-width: 320px;
    max-width: 420px;
  }

  [data-theme="light"] .tx-toast {
    background: #FFFFFF;
    border-color: #E2E8F0;
    box-shadow:
      0 8px 30px rgba(0,0,0,0.08),
      0 2px 8px rgba(0,0,0,0.04);
  }

  /* Variant accent border */
  .tx-toast.tx-success { border-left: 3px solid var(--color-success); }
  .tx-toast.tx-error   { border-left: 3px solid var(--color-error); }
  .tx-toast.tx-warning { border-left: 3px solid var(--color-warning); }
  .tx-toast.tx-info    { border-left: 3px solid var(--color-info); }

  /* Entrance / Exit */
  .tx-toast[data-state="open"] {
    animation: tx-in 350ms cubic-bezier(0.16,1,0.3,1);
  }
  .tx-toast[data-state="closed"] {
    animation: tx-out 200ms ease-in forwards;
  }

  @keyframes tx-in {
    from {
      transform: translateX(110%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes tx-out {
    from { transform: translateX(0); opacity: 1; }
    to   { transform: translateX(110%); opacity: 0; }
  }

  /* ── Icon box ── */
  .tx-icon-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 3px;
    flex-shrink: 0;
    animation: tx-icon-pop 350ms cubic-bezier(0.34,1.56,0.64,1) 150ms both;
  }

  @keyframes tx-icon-pop {
    0%  { transform: scale(0); }
    60% { transform: scale(1.1); }
    100%{ transform: scale(1); }
  }

  .tx-icon-success {
    background: rgba(16,185,129,0.12);
    color: var(--color-success);
  }
  .tx-icon-error {
    background: rgba(220,38,38,0.12);
    color: var(--color-error);
  }
  .tx-icon-warning {
    background: rgba(245,158,11,0.12);
    color: var(--color-warning);
  }
  .tx-icon-info {
    background: rgba(59,130,246,0.12);
    color: var(--color-info);
  }

  [data-theme="light"] .tx-icon-success { background: rgba(5,150,105,0.08); }
  [data-theme="light"] .tx-icon-error   { background: rgba(220,38,38,0.08); }
  [data-theme="light"] .tx-icon-warning { background: rgba(217,119,6,0.08); }
  [data-theme="light"] .tx-icon-info    { background: rgba(37,99,235,0.08); }

  /* ── Content ── */
  .tx-content {
    position: relative;
    flex: 1;
    min-width: 0;
    padding-top: 2px;
    user-select: text;
    -webkit-user-select: text;
    cursor: text;
  }
  .tx-title {
    font-family: var(--font-heading);
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 3px;
    line-height: 1.3;
    user-select: text;
    -webkit-user-select: text;
  }
  .tx-message {
    font-size: 13px;
    color: var(--color-text-secondary);
    line-height: 1.45;
    word-wrap: break-word;
    user-select: text;
    -webkit-user-select: text;
  }

  /* Copied flash + badge */
  .tx-copied-flash {
    animation: tx-flash 300ms ease;
  }
  @keyframes tx-flash {
    0%   { opacity: 1; }
    50%  { opacity: 0.6; }
    100% { opacity: 1; }
  }
  .tx-copied-badge {
    display: inline-block;
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--color-success);
    animation: tx-badge-in 200ms ease;
  }
  @keyframes tx-badge-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Close button ── */
  .tx-close {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    border-radius: 3px;
    transition: all 180ms ease;
  }
  .tx-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
    transform: scale(1.1);
  }
  .tx-close:focus-visible {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }

  /* ── Progress ── */
  .tx-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--color-bg-tertiary);
    border-radius: 0 0 3px 3px;
    overflow: hidden;
  }
  .tx-progress-bar {
    height: 100%;
    border-radius: 0 0 0 3px;
    transition: width 50ms linear;
  }
  .tx-bar-success { background: linear-gradient(90deg, var(--color-success), #34D399); }
  .tx-bar-error   { background: linear-gradient(90deg, var(--color-error), #F87171); }
  .tx-bar-warning { background: linear-gradient(90deg, var(--color-warning), #FCD34D); }
  .tx-bar-info    { background: linear-gradient(90deg, var(--color-info), #93C5FD); }

  /* ── Reduced motion ── */
  @media (prefers-reduced-motion: reduce) {
    .tx-toast,
    .tx-icon-box,
    .tx-close {
      animation: none !important;
      transition-duration: 0ms !important;
    }
  }
`;

/**
 * Toast viewport
 */
export const ToastViewport: React.FC = () => {
  return (
    <>
      <ToastPrimitive.Viewport className="tx-viewport" />
      <style>{`
        .tx-viewport {
          position: fixed;
          top: 16px;
          right: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          width: auto;
          max-width: 100vw;
          max-height: 100vh;
          padding: 0;
          margin: 0;
          list-style: none;
          z-index: 9999;
          outline: none;
        }

        @media (max-width: 640px) {
          .tx-viewport {
            top: auto;
            bottom: 16px;
            left: 16px;
            right: 16px;
          }
          .tx-toast {
            min-width: auto;
            max-width: none;
          }
        }
      `}</style>
    </>
  );
};

export { ToastPrimitive };
